import { apiRequest } from '@/api/client';
import { pushWarning } from '@/lib/warningsBuffer';
import { nowAdjusted } from '@/playback/timeline';
import type { PlayerSignal, PlayerSignalsPayload, VenueSignalKind } from '@signage42/shared-types';
import type { SignalEventPayload } from '@/telemetry/sse';

/**
 * The active venue signal, held on the device.
 *
 * A signal takes over the whole screen, and it has to do so within seconds of
 * being raised, so this state is fed from two sources at once:
 *
 *  - the event channel, which is how "the interval started twenty seconds ago"
 *    reaches a screen at all; and
 *  - `GET /player/signals`, read before playback begins and polled after, which
 *    is what makes a reboot mid-interval resume the override and what covers a
 *    signal raised while the event channel was down.
 *
 * Neither source is trusted to be complete. The fetch is authoritative — it
 * carries every signal the server thinks covers this screen — while an event is
 * an increment applied to whatever we hold. Applying both to one map, keyed by
 * signal id, means a duplicate delivery is idempotent and a missed one is
 * repaired by the next poll rather than stranding the screen.
 *
 * Expiry is enforced locally against the server-corrected clock. That is the
 * failsafe (D9): if this device never hears another word — no `expired` event,
 * no successful fetch — the override still lifts at the expiry it was given,
 * because a foyer board reading "Pause" at 23:40 with the audience long gone is
 * the failure the whole design is aimed at.
 */

/**
 * The fixed override order, highest first (D9). In code, never operator
 * configurable: a priority *field* is an invitation to configure an emergency
 * into second place. The server sorts its response the same way; this exists
 * because an event arriving while another override is held has to be ranked
 * here, with no request in flight.
 */
const KIND_PRIORITY: Record<VenueSignalKind, number> = {
  emergency: 3,
  delay: 2,
  intermission: 1,
};

/**
 * Backstop poll. The event channel is what delivers a signal promptly; this is
 * what recovers one that was raised while the channel was down. Matched to the
 * heartbeat cadence so signals add no new order of magnitude of traffic per
 * screen.
 */
const REFETCH_INTERVAL_MS = 60_000;
const RETRY_INTERVAL_MS = 15_000;

/**
 * Resolves a message locale map on the device, for signals arriving on the
 * event channel. The endpoint resolves `message` server-side through the full
 * locale chain; the event carries the raw map, because one publish fans out to
 * every screen in scope and cannot be resolved per screen. The rule mirrors the
 * server's — exact locale, then same language, then anything — and "anything" is
 * intentional for the same reason it is in the programme (D13): an evacuation
 * notice in the wrong language beats no notice.
 */
function resolveMessage(map: Record<string, string> | null, locale: string | null): string | null {
  if (map === null) return null;
  const entries = Object.entries(map);
  if (entries.length === 0) return null;
  if (locale !== null) {
    const exact = map[locale];
    if (exact !== undefined) return exact;
    const language = locale.split('-')[0]!;
    const byLanguage = entries.find(([key]) => key.split('-')[0] === language);
    if (byLanguage) return byLanguage[1];
  }
  return entries[0]![1];
}

export interface ActiveSignal {
  id: string;
  kind: VenueSignalKind;
  /** Content to render while this signal holds, or null when unconfigured. */
  playlistId: string | null;
  expiresAtMs: number;
  startedAtMs: number;
  message: string | null;
  performanceId: string | null;
}

function toActive(signal: PlayerSignal): ActiveSignal {
  return {
    id: signal.id,
    kind: signal.kind,
    playlistId: signal.playlistId,
    expiresAtMs: Date.parse(signal.expiresAt),
    startedAtMs: Date.parse(signal.startedAt),
    message: signal.message,
    performanceId: signal.performanceId,
  };
}

/**
 * Ranks two live signals. Kind decides; a tie goes to the more recently raised,
 * which is the one somebody raised knowing about the other.
 */
function outranks(a: ActiveSignal, b: ActiveSignal): boolean {
  const byKind = KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind];
  if (byKind !== 0) return byKind > 0;
  return a.startedAtMs > b.startedAtMs;
}

export class SignalCache {
  private signals = new Map<string, ActiveSignal>();
  private emergencyPlaylistId: string | null = null;
  /** The locale the server resolves `message` in, learned from the fetch payload. */
  private locale: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<(signal: ActiveSignal | null) => void>();
  private readonly emergencyListeners = new Set<(playlistId: string | null) => void>();
  private lastNotified: string | null = null;

  /**
   * Reads the active signals before playback starts, so a player restarting
   * during an interval renders the override rather than a minute of the
   * programme it should not be showing.
   */
  async start(): Promise<void> {
    await this.refetch();
  }

  stop(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.timer = null;
    this.expiryTimer = null;
  }

  /** The override in force right now, or null when the timeline should play. */
  active(): ActiveSignal | null {
    const now = nowAdjusted();
    let winner: ActiveSignal | null = null;
    for (const signal of this.signals.values()) {
      if (signal.expiresAtMs <= now) continue;
      // A signal whose kind has no configured content cannot override anything:
      // there is nothing to put on the screen, and a black foyer board is worse
      // than the programme it would have replaced.
      if (signal.playlistId === null) continue;
      if (winner === null || outranks(signal, winner)) winner = signal;
    }
    return winner;
  }

  /**
   * The screen's emergency content, known whether or not an emergency is
   * happening, so it can be pinned in advance (D10).
   */
  emergencyPlaylist(): string | null {
    return this.emergencyPlaylistId;
  }

  subscribe(fn: (signal: ActiveSignal | null) => void): () => void {
    this.listeners.add(fn);
    fn(this.active());
    return () => {
      this.listeners.delete(fn);
    };
  }

  subscribeEmergencyPlaylist(fn: (playlistId: string | null) => void): () => void {
    this.emergencyListeners.add(fn);
    fn(this.emergencyPlaylistId);
    return () => {
      this.emergencyListeners.delete(fn);
    };
  }

  /**
   * Applies one transition from the event channel.
   *
   * The event carries the whole signal, so no follow-up request is needed — which
   * matters because the network that request would use is exactly what may be
   * unavailable during an emergency (D9). An event that ends a signal drops it
   * from the map rather than marking it, since an ended signal has no further
   * bearing on anything.
   *
   * One thing the event cannot carry is the override playlist: resolving it needs
   * the screen's group memberships. For an emergency we already hold the answer,
   * pinned in advance; for the other kinds a fetch follows, and until it lands the
   * signal is held without content and changes nothing on glass.
   */
  applyEvent(payload: SignalEventPayload): void {
    const ended = !payload.active || payload.endedAt !== null;
    if (ended) {
      this.signals.delete(payload.signalId);
    } else {
      const existing = this.signals.get(payload.signalId);
      this.signals.set(payload.signalId, {
        id: payload.signalId,
        kind: payload.kind,
        playlistId:
          payload.kind === 'emergency' ? this.emergencyPlaylistId : (existing?.playlistId ?? null),
        expiresAtMs: Date.parse(payload.expiresAt),
        startedAtMs: Date.parse(payload.startedAt),
        // A repeat may carry a fresher message, but silence is not a retraction —
        // the same rule the server applies on extend.
        message:
          payload.messageI18n === null
            ? (existing?.message ?? null)
            : resolveMessage(payload.messageI18n, this.locale),
        performanceId: payload.performanceId,
      });
    }
    this.settle();
    // A raise of a kind whose playlist we do not yet know needs the endpoint to
    // fill it in; an extend or a clear does not.
    if (!ended && this.signals.get(payload.signalId)?.playlistId === null) {
      void this.refetch();
    }
  }

  async refetch(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.fetchOnce();
    try {
      await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  private async fetchOnce(): Promise<void> {
    let nextIn = REFETCH_INTERVAL_MS;
    try {
      const payload = await apiRequest<PlayerSignalsPayload>('/player/signals');
      // Authoritative: a signal the server no longer lists is over, however it
      // ended and whether or not we saw the event.
      this.signals = new Map(payload.signals.map((s) => [s.id, toActive(s)]));
      this.emergencyPlaylistId = payload.emergencyPlaylistId;
      this.locale = payload.locale;
      for (const listener of this.emergencyListeners) listener(this.emergencyPlaylistId);
      this.settle();
    } catch (err) {
      // Keep whatever we hold. An override already running is more trustworthy
      // than a dropped request, and every signal expires on its own regardless.
      pushWarning(`signals-fetch-failed:${(err as Error).message ?? 'err'}`);
      nextIn = RETRY_INTERVAL_MS;
    } finally {
      this.schedule(nextIn);
    }
  }

  private schedule(delayMs: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.refetch();
    }, delayMs);
  }

  /**
   * Notifies subscribers if the winning override changed, and arms a timer for
   * the next expiry so the release happens at the expiry rather than at the next
   * poll — up to a minute of a stale "Pause" notice on a public wall otherwise.
   */
  private settle(): void {
    const winner = this.active();
    const key =
      winner === null ? null : `${winner.id}:${winner.playlistId ?? ''}:${winner.message ?? ''}`;
    if (key !== this.lastNotified) {
      this.lastNotified = key;
      for (const listener of this.listeners) listener(winner);
    }
    this.armExpiryTimer();
  }

  private armExpiryTimer(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    const now = nowAdjusted();
    let soonest = Number.POSITIVE_INFINITY;
    for (const signal of this.signals.values()) {
      if (signal.expiresAtMs > now) soonest = Math.min(soonest, signal.expiresAtMs);
    }
    if (!Number.isFinite(soonest)) return;
    this.expiryTimer = setTimeout(
      () => {
        // Drop what has lapsed and re-settle; this is the failsafe firing with
        // no help from the server at all.
        for (const [id, signal] of this.signals) {
          if (signal.expiresAtMs <= nowAdjusted()) this.signals.delete(id);
        }
        this.settle();
      },
      Math.max(250, soonest - now + 50),
    );
  }
}
